/* eslint-disable @typescript-eslint/no-explicit-any */
import Container from "../components/Container";
import Text from "../components/Text";
import Image from "../components/Image";
import logoFull from "../assets/images/logo-full.png";
import logo from "../assets/images/simbolo.png";
import { useSecureContext } from "../contexts/SecureContext";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Badge } from "primereact/badge";
import { onMessage } from "@firebase/messaging";
import { messaging } from "../../firebase";
import { useNotificationContext } from "../contexts/NotificationContext";
import { Sidebar } from "primereact/sidebar";
import moment from "moment";
import "moment/locale/pt-br";

// Configurar locale globalmente
moment.locale("pt-br");

import Alert from "../components/Alert";
import { Button } from "primereact/button";
import { useAssetSidebarContext } from "../contexts/AssetSidebarContext";
import { useAuthContext } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import Icon from "../components/Icon";
import { useMediaQuery } from "react-responsive";
import Show from "../components/Show";
import InsightsSidebar from "../components/InsightsSidebar";
import InvestorProfileForm from "../components/InvestorProfileForm";
import { useTaxImportContext } from "../contexts/TaxImportContext";
import { Tracker } from "../analytics";

interface INotificationSidebarProps {
    notificationOpen: boolean;
    setNotificationsOpen: Dispatch<SetStateAction<boolean>>;
}

const NotificationSidebar = ({ notificationOpen, setNotificationsOpen }: INotificationSidebarProps) => {
    const notificationContext = useNotificationContext();

    useEffect(() => {
        moment.locale("pt-br");
        const body = document.getElementsByTagName("body")[0];
        body.style.overflow = "hidden";

        return () => {
            body.style.overflow = "auto";
        }
    }, []);

    const hasNotifications = notificationContext.notificationQuery.data && Object.keys(notificationContext.notificationQuery.data).length > 0;
    const isLoading = notificationContext.notificationQuery.isLoading;

    return (
        <Sidebar
            header={
                <Container between itemsCenter className="w-full py-3">
                    <Container gap={3} itemsCenter>
                        <div className="flex align-items-center justify-content-center bg-primary border-circle" style={{ width: '2.5rem', height: '2.5rem' }}>
                            <i className="pi pi-bell text-white text-xl"></i>
                        </div>
                        <div>
                            <h3 className="m-0 text-900 font-semibold text-xl">Notificações</h3>
                            {hasNotifications && (
                                <Text color="500" size="sm" className="mt-1">
                                    {Object.values(notificationContext.notificationQuery.data || {}).reduce((acc, notifications) => acc + notifications.length, 0)} {Object.values(notificationContext.notificationQuery.data || {}).reduce((acc, notifications) => acc + notifications.length, 0) === 1 ? 'notificação' : 'notificações'}
                                </Text>
                            )}
                        </div>
                    </Container>
                </Container>
            }
            visible={notificationOpen}
            position="right"
            onHide={() => setNotificationsOpen(false)}
            className="notification-sidebar"
            style={{ width: '28rem' }}
        >
            <div className="notification-content" style={{ height: '100%', overflowY: 'auto' }}>
                {isLoading ? (
                    <Container center fullHeight className="py-8">
                        <i className="pi pi-spin pi-spinner text-4xl text-400"></i>
                    </Container>
                ) : hasNotifications ? (
                    <div className="px-2">
                        {Object.keys(notificationContext.notificationQuery.data || {}).map((key, index) => {
                            const dateMoment = moment(key, "YYYY-MM-DD");
                            const isToday = dateMoment.isSame(new Date(), "day");
                            const isYesterday = dateMoment.isSame(moment(new Date()).add(-1, "day"), "day");
                            
                            // Mapeamento de meses em português
                            const meses = [
                                "janeiro", "fevereiro", "março", "abril", "maio", "junho",
                                "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"
                            ];
                            
                            const formatDate = () => {
                                const day = dateMoment.format("DD");
                                const monthIndex = dateMoment.month();
                                return `${day} de ${meses[monthIndex]}`;
                            };
                            
                            return (
                                <div key={key} className="notification-group" style={{ 
                                    marginTop: index === 0 ? '0.5rem' : '2rem',
                                    animation: 'fadeIn 0.3s ease-in'
                                }}>
                                    <div className="flex align-items-center gap-2 mb-3">
                                        <div className="flex-1 border-top-1 border-200"></div>
                                        <div className="px-3 py-1 bg-100 border-round">
                                            <Text 
                                                color="700" 
                                                size="sm" 
                                                fontWeight="semibold"
                                                className="text-uppercase"
                                            >
                                                {
                                                    isToday
                                                        ? "Hoje"
                                                        : isYesterday
                                                            ? "Ontem"
                                                            : formatDate()
                                                }
                                            </Text>
                                        </div>
                                        <div className="flex-1 border-top-1 border-200"></div>
                                    </div>
                                    <div className="flex flex-column gap-2">
                                        {notificationContext.notificationQuery.data?.[key].map && 
                                            notificationContext.notificationQuery.data[key].map((notification, notifIndex) => (
                                                <div 
                                                    key={notification.id}
                                                    style={{ 
                                                        animation: `fadeIn 0.3s ease-in ${notifIndex * 0.05}s both`
                                                    }}
                                                >
                                                    <Alert notification={notification} />
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <Container center flexColumn fullHeight className="py-8 px-4" gap={3}>
                        <div className="flex align-items-center justify-content-center bg-100 border-circle" style={{ width: '5rem', height: '5rem' }}>
                            <i className="pi pi-bell-slash text-4xl text-400"></i>
                        </div>
                        <Text color="600" size="lg" fontWeight="semibold" center>
                            Nenhuma notificação
                        </Text>
                        <Text color="500" size="sm" center className="max-w-20rem">
                            Você está em dia! Quando houver novas notificações, elas aparecerão aqui.
                        </Text>
                    </Container>
                )}
            </div>
            
            <style>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .notification-sidebar .p-sidebar-content {
                    padding: 0 !important;
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                }
                
                .notification-content {
                    flex: 1;
                    padding: 1rem 0;
                }
                
                .notification-content::-webkit-scrollbar {
                    width: 6px;
                }
                
                .notification-content::-webkit-scrollbar-track {
                    background: transparent;
                }
                
                .notification-content::-webkit-scrollbar-thumb {
                    background: #d1d5db;
                    border-radius: 3px;
                }
                
                .notification-content::-webkit-scrollbar-thumb:hover {
                    background: #9ca3af;
                }
            `}</style>
        </Sidebar>
    )
}

export default function Header() {
    const secureContext = useSecureContext();
    const [countMessages, setCountMessages] = useState(0);
    const [notificationOpen, setNotificationsOpen] = useState(false);
    const assetSidebarContext = useAssetSidebarContext();
    const auth = useAuthContext();
    const navigate = useNavigate();
    const location = useLocation();
    const screen = location.pathname;
    const isTablet = useMediaQuery({ minWidth: 569, maxWidth: 768 });
    const isMobile = useMediaQuery({ maxWidth: 568 });
    const [showInsights, setShowInsights] = useState(false);
    const [investorProfileOpen, setInvestorProfileOpen] = useState(false);
    const { toggleImportTax } = useTaxImportContext();

    onMessage(messaging, (payload) => {
        if (Notification.permission == 'granted') {
            new Notification(payload.notification?.title ?? "", { body: payload.notification?.body, icon: logo });
        }

        setCountMessages(countMessages => countMessages + 1);
    });

    return (
        <>
            <InsightsSidebar
                insightsOpen={showInsights}
                setInsightsOpen={setShowInsights}
                setInvestorProfileOpen={setInvestorProfileOpen}
            />

            <Sidebar
                visible={investorProfileOpen}
                position="right"
                onHide={() => setInvestorProfileOpen(false)}
            >
                <InvestorProfileForm source="header_insights" />
            </Sidebar>

            {
                notificationOpen && (
                    <NotificationSidebar
                        notificationOpen={notificationOpen}
                        setNotificationsOpen={setNotificationsOpen}
                    />
                )
            }

            <Container padding="4" between gap={3} wrap itemsCenter>
                <Container fullWidth gap={3} className="flex-1">
                    <Image src={logoFull} width={200} />
                </Container>

                <Container wrap fullWidth gap={3} className={`flex-1 justify-content-end align-items-center`}>
                    <i
                        className="pi pi-file-import p-overlay-badge cursor-pointer"
                        style={{ fontSize: '1.5rem' }}
                        title="Importação DIRPF"
                        onClick={() => {
                            Tracker.trackCta('import_dirpf', screen);
                            toggleImportTax();
                        }}
                    ></i>

                    <i
                        className="pi pi-sparkles p-overlay-badge cursor-pointer"
                        style={{ fontSize: '1.5rem' }}
                        title="Insights da carteira"
                        onClick={() => {
                            Tracker.trackCta('open_insights', screen);
                            setShowInsights(old => !old);
                        }}
                    ></i>
                    
                    <i
                        className="pi pi-bell p-overlay-badge cursor-pointer"
                        style={{ fontSize: '1.5rem' }}
                        title="Notificações"
                        onClick={() => {
                            Tracker.trackCta('open_notifications', screen);
                            setNotificationsOpen(true);
                            setCountMessages(0);
                        }}
                    >
                        {
                            countMessages > 0 && (
                                <Badge value={countMessages}></Badge>
                            )
                        }
                    </i>

                    <i
                        className={!secureContext.isSecure ? 'pi pi-eye p-overlay-badge cursor-pointer' : 'pi pi-eye-slash p-overlay-badge cursor-pointer'}
                        style={{ fontSize: '1.5rem' }}
                        title={!secureContext.isSecure ? 'Esconder valores' : 'Ver valores'}
                        onClick={() => secureContext.toggleSecure()}
                    ></i>

                    <Show when={isMobile}>
                        <Icon
                            name="pi-sign-out cursor-pointer font-bold"
                            title="Sair"
                            onClick={(e) => {
                                e.preventDefault();
                                void auth.logout();
                            }}
                        />

                        <Icon
                            name="pi-user cursor-pointer font-bold"
                            title="Perfil"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate("/profile");
                            }}
                        />
                    </Show>

                    <Show when={isTablet || isMobile}>
                        <Icon
                            name="pi-folder-open cursor-pointer font-bold"
                            title="Cadastrar ativo"
                            onClick={(e) => {
                                e.preventDefault();
                                Tracker.trackCta('open_asset_sidebar', screen);
                                assetSidebarContext.openAssetSidebar();
                            }}
                        />
                    </Show>

                    <Show when={!isTablet && !isMobile}>
                        <Button 
                            icon="pi pi-folder-open"
                            className="flex gap-1"
                            onClick={() => {
                                Tracker.trackCta('open_asset_sidebar', screen);
                                assetSidebarContext.openAssetSidebar();
                            }}
                            outlined
                        >
                            Cadastrar ativo
                        </Button>
                    </Show>
                </Container>
            </Container>
        </>
    )
}
